import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MapPin, Users, Clock, Search, Building2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VenuesPage = () => {
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('all');

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const response = await axios.get(`${API}/venues`);
        setVenues(response.data);
      } catch (error) {
        console.error('Error fetching venues:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVenues();
  }, []);

  const filteredVenues = venues.filter(venue => {
    const matchesSearch = venue.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         venue.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCity = cityFilter === 'all' || venue.city === cityFilter;
    return matchesSearch && matchesCity;
  });

  const cities = [...new Set(venues.map(v => v.city))];

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-4">Our Venues</h1>
          <p className="text-muted-foreground max-w-2xl">
            Discover premium workspaces across multiple cities. Each location offers state-of-the-art amenities 
            and a vibrant community.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search venues..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-input/50"
              data-testid="venue-search-input"
            />
          </div>
          <Select value={cityFilter} onValueChange={setCityFilter}>
            <SelectTrigger className="w-full sm:w-48 bg-input/50" data-testid="city-filter-select">
              <SelectValue placeholder="Filter by city" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cities</SelectItem>
              {cities.map(city => (
                <SelectItem key={city} value={city}>{city}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Venues Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredVenues.length === 0 ? (
          <div className="text-center py-20">
            <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">No venues found</h3>
            <p className="text-muted-foreground">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVenues.map(venue => (
              <Card 
                key={venue.id}
                className="bg-card border-border hover:border-primary/50 transition-all duration-300 overflow-hidden group cursor-pointer"
                onClick={() => navigate(`/venues/${venue.id}`)}
                data-testid={`venue-card-${venue.id}`}
              >
                <div className="relative h-56 overflow-hidden rounded-t-lg">
                  <img 
                    src={venue.images?.[0] 
                      ? (venue.images[0].startsWith('/') ? `${BACKEND_URL}${venue.images[0]}` : venue.images[0])
                      : 'https://images.unsplash.com/photo-1497366412874-3415097a27e7?w=800'}
                    alt={venue.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1497366412874-3415097a27e7?w=800'; }}
                  />
                  <Badge className="absolute top-4 right-4 bg-primary/90">{venue.city}</Badge>
                </div>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <h3 className="font-heading font-semibold text-xl group-hover:text-primary transition-colors mb-2">
                      {venue.name}
                    </h3>
                    <div className="flex items-center text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4 mr-1 text-primary shrink-0" />
                      <span className="line-clamp-1">{venue.address}</span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {venue.description}
                  </p>

                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1 text-primary" />
                      {venue.opening_time} - {venue.closing_time}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {venue.amenities?.slice(0, 4).map((amenity, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {amenity}
                      </Badge>
                    ))}
                    {venue.amenities?.length > 4 && (
                      <Badge variant="secondary" className="text-xs">
                        +{venue.amenities.length - 4} more
                      </Badge>
                    )}
                  </div>

                  <Button 
                    className="w-full bg-primary hover:bg-primary/90 mt-4"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/booking?venue=${venue.id}`);
                    }}
                    data-testid={`book-venue-${venue.id}-btn`}
                  >
                    Book Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VenuesPage;
